import React, { useState } from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { ReportedMessageBubble } from './ReportedMessageBubble'
import type { ReportMessageContext } from '@warcarp/api-types'

export interface PlayerSafetyChoice {
  blocked: boolean
  report: boolean
  reason: string
}

function SafetyCheckbox({ label, checked, disabled, onPress }: {
  label: string; checked: boolean; disabled: boolean; onPress: () => void
}) {
  return <TouchableOpacity accessibilityRole="checkbox" accessibilityLabel={label}
    aria-checked={checked}
    accessibilityState={{ checked, disabled }} disabled={disabled} onPress={onPress}
    className="flex-row items-center gap-4 py-3" style={{ minHeight: 64 }}>
    <View style={{ width: 40, height: 40, flexShrink: 0, borderWidth: 2, borderRadius: 6 }}
      className={`items-center justify-center ${checked ? 'bg-blood border-ink' : 'bg-paper-up border-olive'}`}>
      <Text className="text-paper text-2xl">{checked ? '✓' : ''}</Text>
    </View>
    <Text className="font-stencil text-ink text-lg">{label}</Text>
  </TouchableOpacity>
}

export function PlayerSafetyForm({ username, alreadyBlocked, busy, ready = true, canBlock = true, reportOnly = false, error, messageContext, onSubmit, onCancel }: {
  messageContext?: ReportMessageContext
  username: string; alreadyBlocked: boolean; busy: boolean; ready?: boolean; canBlock?: boolean; reportOnly?: boolean; error?: string
  onSubmit: (choice: PlayerSafetyChoice) => void; onCancel: () => void
}) {
  const [blocked, setBlocked] = useState(!reportOnly && canBlock && !messageContext)
  const [report, setReport] = useState((reportOnly || !canBlock || !!messageContext) && !messageContext?.alreadyReported)
  const [reason, setReason] = useState('')
  const canSubmit = ready && !busy && (reportOnly ? !!reason.trim() : (messageContext ? (blocked && !alreadyBlocked) || report : blocked !== alreadyBlocked || report) && (!report || !!reason.trim()))
  return <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-3">
    <Text className="font-stencil text-ink text-lg">{reportOnly ? `Report @${username}` : messageContext ? 'Flag as inappropriate' : `Block or report @${username}`}</Text>
    {messageContext && <View className="gap-2">
      <ReportedMessageBubble message={messageContext} />
    </View>}
    {!reportOnly && <>
      <SafetyCheckbox label="Block user" checked={blocked} disabled={busy || !canBlock} onPress={() => setBlocked(!blocked)} />
      <Text className="font-typed text-ink-soft">{!canBlock ? 'Bots can be reported, but cannot be blocked.' : messageContext && alreadyBlocked ? 'This player is already blocked. Reporting will not change that.' : alreadyBlocked
        ? 'This player is blocked. Uncheck Block and submit to unblock them.'
        : 'Stop direct messages and game invites from this player. You can unblock them later.'}</Text>
    </>}
    {messageContext?.alreadyReported && <Text accessibilityRole="alert" className="font-typed text-ink">You’ve already flagged this message.</Text>}
    {!reportOnly && <SafetyCheckbox label="Report user" checked={report} disabled={busy || !!messageContext?.alreadyReported} onPress={() => setReport(!report)} />}
    {(report || reportOnly) && <>
      <Text className="font-typed text-ink-soft">Tell the WarCarp team what happened. Reports are private. Include the game name or a link to the game/chat if relevant.</Text>
      {messageContext && <Text className="font-typed text-ink-soft">The message above will be included in your report.</Text>}
      <Input testID="report-details" accessibilityLabel="Report details" placeholder="What happened?" multiline
        textAlignVertical="top" style={{ minHeight: 140 }}
        value={reason} onChangeText={setReason} editable={!busy} maxLength={2000} />
    </>}
    {error && <Text className="font-typed text-blood">{error}</Text>}
    <Button testID="submit-player-safety" title="Submit" loading={busy} disabled={!canSubmit}
      onPress={() => { if (canSubmit) onSubmit({ blocked, report, reason: reason.trim() }) }} />
    <Button title="Cancel" variant="ghost" disabled={busy} onPress={onCancel} />
  </View>
}
